import { addCurrency } from "../utils/currencyUtils";
import { combineStatements, tournamentStatement } from "./financeYear";
import { downloadBlob } from "./exportPayoutTable";
import {
  eventEntryFee,
  signupAdvertisedTotal,
  signupMoney,
  signupPlacement,
} from "./signupDashboard";

const money = (signup) => signupAdvertisedTotal(signup);

const sumMoney = (signups, predicate) =>
  addCurrency(
    ...signups.filter(predicate).map((signup) => money(signup))
  );

/**
 * Totals from the same signup rows the Signup dashboard already shows.
 * Entry fees only. Card processing and the platform fee are not included.
 * Online collected is card payments still marked Paid.
 */
export const tournamentMoneyTotals = (signups = []) => {
  const rows = signups || [];
  const onlineCollected = sumMoney(rows, (signup) => signupMoney(signup) === "paid");
  const deskCollected = sumMoney(
    rows,
    (signup) => signupMoney(signup) === "walk_up"
  );
  const dueAtDesk = sumMoney(rows, (signup) => {
    const state = signupMoney(signup);
    return state === "due" || state === "held";
  });
  const refunds = sumMoney(
    rows,
    (signup) => (signup?.paymentStatus || "unpaid") === "refunded"
  );

  return {
    entries: rows.filter((signup) => signupPlacement(signup) === "on_squad")
      .length,
    onlineCollected,
    deskCollected,
    dueAtDesk,
    refunds,
    showCardDesk: onlineCollected > 0 || deskCollected > 0,
  };
};

const emptyMoneyIn = () => ({
  entryFees: 0,
  sidePots: 0,
  brackets: 0,
  total: 0,
});

/** On-squad signup amounts, paid or not. Refunded rows are left out. */
export const signupMoneyIn = (signup) => {
  if (signupPlacement(signup) !== "on_squad") return null;
  if ((signup?.paymentStatus || "unpaid") === "refunded") return null;
  const bowlers = signup?.signupBowlers || signup?.bowlers || [];
  const bowlerCount = Math.max(bowlers.length, 1);
  const entryFees = eventEntryFee(signup?.tournamentEvents) * bowlerCount;
  const sidePots = bowlers.reduce(
    (sum, bowler) => sum + (Number(bowler.totalSidepotCost) || 0),
    0
  );
  const brackets = bowlers.reduce(
    (sum, bowler) => sum + (Number(bowler.totalBracketCost) || 0),
    0
  );
  return {
    entryFees,
    sidePots,
    brackets,
    total: addCurrency(entryFees, sidePots, brackets),
  };
};

export const moneyInTotals = (signups = []) => {
  const totals = emptyMoneyIn();
  (signups || []).forEach((signup) => {
    const parts = signupMoneyIn(signup);
    if (!parts) return;
    totals.entryFees = addCurrency(totals.entryFees, parts.entryFees);
    totals.sidePots = addCurrency(totals.sidePots, parts.sidePots);
    totals.brackets = addCurrency(totals.brackets, parts.brackets);
  });
  totals.total = addCurrency(totals.entryFees, totals.sidePots, totals.brackets);
  return totals;
};

/** Teams created at the desk, not linked to an online signup. */
export const walkUpEntryCount = (teams = [], signups = []) => {
  const linked = new Set(
    (signups || []).map((signup) => signup?.teamID).filter(Boolean)
  );
  return (teams || []).filter(
    (team) => team && !team.isDeleted && team.id && !linked.has(team.id)
  ).length;
};

export const bowlerIdentity = (bowler) => {
  const matched = String(bowler?.matchedBowlerId || "").trim();
  if (matched) return `bowler:${matched}`;
  const usbc = String(bowler?.usbcID || bowler?.usbcId || "").trim();
  if (usbc) return `usbc:${usbc.toLowerCase()}`;
  const name = String(bowler?.name || "").trim().toLowerCase();
  if (name) return `name:${name}`;
  return null;
};

export const onSquadBowlers = (signups = []) =>
  (signups || [])
    .filter((signup) => signupPlacement(signup) === "on_squad")
    .flatMap((signup) => signup.signupBowlers || signup.bowlers || []);

export const lockedPayoutTotal = (eventDetails) =>
  addCurrency(
    ...Object.values(eventDetails || {}).flatMap((details) =>
      (details?.selectedPayout?.payouts || []).map((payout) => payout.amount)
    )
  );

const csvCell = (value) => {
  const text = value == null ? "" : String(value);
  if (/[",\r\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

const csvText = (rows) =>
  rows.map((row) => row.map(csvCell).join(",")).join("\r\n");

const dollars = (amount) => (Number(amount) || 0).toFixed(2);

export const tournamentTotalsCsv = (totals, tournamentName) =>
  csvText([
    ["Tournament", tournamentName || ""],
    ["Entries", totals.entries],
    ["Online collected", dollars(totals.onlineCollected)],
    ["Desk collected", dollars(totals.deskCollected)],
    ["Due at desk", dollars(totals.dueAtDesk)],
    ["Refunds", dollars(totals.refunds)],
  ]);

export const yearTotalsCsv = ({ year, rollup, finance = null }) => {
  const lineageLabel = finance?.lineageLabel || "Linage";
  const feeById = new Map(
    (finance?.tournaments || []).map((tournament) => [tournament.id, tournament])
  );
  const statements = (rollup.tournaments || []).map((tournament) =>
    tournamentStatement(tournament.moneyIn, feeById.get(tournament.id) || null)
  );
  const statement = combineStatements(statements);
  const onlineEntries = Number(rollup.entries) || 0;
  const walkUpEntries =
    rollup.walkUpEntries == null ? null : Number(rollup.walkUpEntries) || 0;
  const rows = [
    ["Year", year],
    ["Tournaments", rollup.tournamentCount],
    [
      "Entries",
      walkUpEntries == null ? onlineEntries : onlineEntries + walkUpEntries,
    ],
    ["Online entries", onlineEntries],
    ["Walk-up entries", walkUpEntries == null ? "" : walkUpEntries],
    ["Unique bowlers", rollup.uniqueBowlers],
    ["Online payment", dollars(rollup.onlineCollected)],
    ["Online desk", dollars(rollup.deskCollected)],
    ["Online still due", dollars(rollup.dueAtDesk)],
    ["Online refunds", dollars(rollup.refunds)],
    ["Walk-up", dollars(rollup.walkUpCollected)],
  ];
  rows.push(
    ["Entry fees", dollars(statement.entryFees)],
    ["Side pots", dollars(statement.sidePots)],
    ["Brackets", dollars(statement.brackets)],
    ["Money added", dollars(statement.added)],
    ["Money in", dollars(statement.inTotal)]
  );
  if (finance) {
    rows.push(
      [lineageLabel, dollars(statement.lineage)],
      ["Prize Fund", dollars(statement.prizeFund)],
      ["Expenses", dollars(statement.expenses)]
    );
    (statement.otherFees || []).forEach((fee) => {
      rows.push([fee.name, dollars(fee.amount)]);
    });
    if (statement.outBracketPrize > 0) {
      rows.push(["Bracket prize fund", dollars(statement.outBracketPrize)]);
    }
    if (statement.outBracketFees > 0) {
      rows.push(["Bracket fees", dollars(statement.outBracketFees)]);
    }
    const bracketRest =
      Math.round(
        ((Number(statement.outBrackets) || 0) -
          (Number(statement.outBracketPrize) || 0) -
          (Number(statement.outBracketFees) || 0)) *
          100
      ) / 100;
    if (bracketRest > 0) {
      rows.push(["Brackets", dollars(bracketRest)]);
    }
    rows.push(["Money out", dollars(statement.outTotal)]);
    if (finance.lockedPayouts != null) {
      rows.push(["Locked payouts", dollars(finance.lockedPayouts)]);
    }
  } else {
    rows.push(["Money out", dollars(statement.outTotal)]);
  }
  const otherNames = (statement.otherFees || []).map((fee) => fee.name);
  const header = [
    "Tournament",
    "Start date",
    "Entries",
    "Entry fees",
    "Side pots",
    "Brackets",
    "Money added",
    "Money in",
  ];
  if (finance) {
    header.push(
      lineageLabel,
      "Prize Fund",
      "Expenses",
      ...otherNames,
      "Money out",
      "Locked payouts",
      "Finance"
    );
  } else {
    header.push("Money out");
  }
  rows.push([], header);
  (rollup.tournaments || []).forEach((tournament, index) => {
    const books = statements[index];
    const fees = feeById.get(tournament.id);
    const row = [
      tournament.name || "",
      tournament.startDate || "",
      (Number(tournament.entries) || 0) +
        (Number(tournament.walkUpEntries) || 0),
      dollars(books.entryFees),
      dollars(books.sidePots),
      dollars(books.brackets),
      dollars(books.added),
      dollars(books.inTotal),
    ];
    if (finance) {
      const otherByName = new Map(
        (books.otherFees || []).map((fee) => [fee.name, fee.amount])
      );
      row.push(
        fees ? dollars(books.lineage) : "",
        fees ? dollars(books.prizeFund) : "",
        fees ? dollars(books.expenses) : "",
        ...otherNames.map((name) =>
          fees && otherByName.has(name) ? dollars(otherByName.get(name)) : fees ? dollars(0) : ""
        ),
        dollars(books.outTotal),
        fees?.locked && fees.lockedPayouts != null
          ? dollars(fees.lockedPayouts)
          : "",
        fees?.source || ""
      );
    } else {
      row.push(dollars(books.outTotal));
    }
    rows.push(row);
  });
  return csvText(rows);
};

const fileSlug = (name) => {
  const slug = String(name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return slug || "tournament";
};

export const downloadTournamentTotalsCsv = (totals, tournamentName) => {
  downloadBlob(
    `signup-totals-${fileSlug(tournamentName)}.csv`,
    new Blob([tournamentTotalsCsv(totals, tournamentName)], {
      type: "text/csv;charset=utf-8;",
    })
  );
};

export const downloadYearTotalsCsv = ({
  year,
  rollup,
  finance = null,
  organizationName,
}) => {
  downloadBlob(
    `${fileSlug(organizationName)}-${year}.csv`,
    new Blob([yearTotalsCsv({ year, rollup, finance })], {
      type: "text/csv;charset=utf-8;",
    })
  );
};
